import { AppThunk } from '../../../app/store';
import { AgentFirstCartEditVia } from '../../../constants/agentFirstCartEdit';
import { CartItemAdditionTypes } from '../../../constants/event';
import { cartActions } from '../../../reducers/cartSlice';
import { dialogActions } from '../../../reducers/dialogSlice';
import { CartItem } from '../../../utils/cart';
import logger from '../../../utils/logger';
import { DRAFT_CART_SCOPE, MAIN_CART_SCOPE } from './cartScope';
import { draftCartActions } from './draftCart.slice';
import { dismissAllUnresolvedItems } from '../unresolvedItems/unresolvedItems.slice';

/**
 * A line the auto-combo machinery assembled out of OTHER cart lines.
 *
 * Its `autoCombinedCartItemIds` address `cartSlice`'s id space, so they mean
 * nothing once the line is sitting in a draft that numbers from 1 independently.
 *
 * Keyed on the ITEM's own data, deliberately not on `isCartItemAutoCombined` —
 * that selector ANDs in the auto-combo feature flags, so with those off a line
 * still carrying the ids would read as ordinary and be copied with them
 * silently stripped. Whether the ids are HONOURED is a feature decision;
 * whether they EXIST is a fact about the line.
 *
 * Shared by the two places that have to agree: the per-item control's render
 * gate in `CartItemReview`, and the copy paths below. Two copies of this
 * predicate is precisely the `isAutoCombined` / `autoCombinedCartItemIds`
 * divergence already logged against this feature.
 */
export const isAutoCombinedLine = (cartItem: CartItem): boolean =>
  !!(cartItem.autoCombinedCartItemIds?.length || cartItem.isAutoCombined);

/**
 * A draft-safe copy of an order line, renumbered into the draft's id space.
 *
 * A shallow spread, NOT a JSON round-trip. `CartItem` extends the parsed menu
 * item, so it carries the menu's own `modifierGroups → menuItems →
 * modifierGroups …` tree, whose nodes are shared across branches. Serialising
 * expands every shared node, and on a big item the string overflows —
 * `RangeError: Invalid string length` on Take over. Sharing the nested tree is
 * safe: both slices are Immer state, so an edit to either copies on write and
 * never reaches into the other.
 *
 * `addedBy` is preserved. The line really did come from the AI and saying so is
 * useful in My Cart. It also keeps `addItemToCart`'s upsell handlers off the
 * draft, which replay session-level effects that have no business firing for a
 * copy.
 *
 * The combo markers are dropped here as well as gated on above — the belt to
 * that brace.
 */
const draftCopyOf = (cartItem: CartItem, cartItemId: number): CartItem => {
  const copy: CartItem = { ...cartItem, cartItemId };
  delete copy.autoCombinedCartItemIds;
  delete copy.isAutoCombined;
  return copy;
};

/**
 * Write one order line into the draft, carrying its quantity.
 *
 * The id is minted from the DRAFT's own `sequenceId`, read fresh on each call:
 * the two slices number independently and both start at 1, so reusing the
 * source id would collide the moment a second item arrived — and
 * `addItemToCart` keys on `cartItemId` and OVERWRITES, so a collision is a
 * silent loss rather than an error.
 *
 * `addItemToCart` always sets quantity 1, so anything above that is a second
 * dispatch. Same shape as `writeLineToOrder` in the other direction.
 */
const copyLineIntoDraft =
  (
    cartItem: CartItem,
    quantity: number,
    via: AgentFirstCartEditVia
  ): AppThunk<number> =>
  (dispatch, getState) => {
    const { sequenceId } = getState().draftCart;
    dispatch(
      draftCartActions.addItemToCart({
        cartItem: draftCopyOf(cartItem, sequenceId),
        // HITL-1646: which journey this was. Observability only — the reducer
        // ignores it; `agentFirstCartEditMiddleware` is the sole reader.
        via,
      })
    );
    if (quantity > 1) {
      dispatch(
        draftCartActions.updateQuantity({
          cartItemId: sequenceId.toString(),
          quantity,
        })
      );
    }
    return sequenceId;
  };

/**
 * HITL-1704 — leave the line just copied open in the centre pane, so the agent
 * can type its modifiers straight into the OmniBar.
 *
 * Without this the selection stayed wherever it was before the copy. Usually
 * that was nothing, so the agent had to click the new My Cart line first; and
 * when it was a LIVE ORDER line, the modifiers they picked next were routed by
 * `selectWriteCartScope` into the AI's cart instead of their own — the Doro
 * report of mods "reflecting in the live order directly" after a copy.
 *
 * Read back from `draftCart` rather than built from the source line, so the
 * pane holds exactly the object the draft holds — with its draft id.
 */
const selectDraftLine =
  (draftId: number): AppThunk =>
  (dispatch, getState) => {
    const copied = getState().draftCart.cartItems?.[draftId];
    if (!copied) return;
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: copied,
        itemCartId: draftId,
        cartScope: DRAFT_CART_SCOPE,
      })
    );
  };

/**
 * HITL-1614 — copy one Live Order line into My Cart.
 *
 * The live order is untouched by a copy, which is what makes this safe to run
 * in `Shadow`: it reads `cartSlice` and never writes it.
 *
 * Quantity is deliberately NOT carried: this path has always landed the line at
 * 1 and HITL-1644 leaves it exactly as it was. That it disagrees with the
 * cart-level copy below is real and is raised separately — it is an order
 * accuracy question, not a refactor, and it does not get decided by a helper
 * being shared.
 */
export const copyCartItemToDraft =
  (cartItem: CartItem): AppThunk =>
  (dispatch) => {
    if (!cartItem) return;

    const draftId = dispatch(copyLineIntoDraft(cartItem, 1, 'copy_item'));
    dispatch(selectDraftLine(draftId));

    logger.debug({
      message: 'Copied a Live Order line into My Cart',
      moreInfo: `${cartItem.name} -> draft cartItemId ${draftId}`,
    });
  };

/**
 * HITL-1644 — copy the WHOLE order into My Cart in one action.
 *
 * Reads are granular, writes are wholesale. Pulling the AI's work in costs the
 * customer nothing, so it can be fine-grained; writing the live order is not,
 * so that stays one action with one meaning. This is the granular side's
 * shortcut: correcting one line of a five-item order should not cost five
 * presses in.
 *
 * REPLACES, by CLEARING the draft first — HITL-1649, and a deliberate reversal.
 *
 * HITL-1644 achieved "replace, never append" by refusing outright when the
 * draft was non-empty, which made the control a once-per-cart action: after one
 * press it disabled itself until My Cart was emptied by hand. The agent's
 * question was the right one — pressing a button and having it grey itself out
 * reads as a fault, not as a guard — and the workflow it forced was worse than
 * the risk it avoided: the AI adds a line mid-order, and picking it up meant
 * dismantling My Cart line by line.
 *
 * So each press now re-seeds My Cart from the Live order, and the cost is
 * stated plainly: it DISCARDS whatever the agent had built, with no
 * confirmation. That is the trade this ticket accepted, on the reasoning that
 * the press is deliberate, the label says which direction it runs in, and the
 * tooltip says it replaces. A confirm dialog in the seconds before readback is
 * friction exactly where it costs most.
 *
 * Nothing is cleared when there is nothing to copy. An order with no copyable
 * lines leaves the draft alone rather than emptying it into silence — a press
 * that destroyed the agent's cart and put nothing in its place would be the
 * worst reading of "replace" available.
 *
 * Auto-combined lines are skipped, exactly as the per-item control excludes
 * them. Skipped and not failed: a cart with one combo in it still copies its
 * other four lines, which is the useful answer.
 *
 * Every dispatch is synchronous inside one handler, so React commits them as a
 * single render — My Cart fills in one frame rather than line by line.
 */
export const copyOrderIntoDraftCart = (): AppThunk => (dispatch, getState) => {
  const { cartItems, cartItemsQuantity } = getState().cart;
  // No sort. `cartItemId` keys are integer-like, and JS iterates those in
  // ascending numeric order whatever the insertion order was — so My Cart reads
  // in the order the customer said it without one. A `.sort()` here looked like
  // a guarantee and was dead code: no mutation of it could fail a test.
  const sourceIds = Object.keys(cartItems ?? {});
  const copyableIds = sourceIds.filter(
    (sourceId) =>
      cartItems[sourceId] && !isAutoCombinedLine(cartItems[sourceId])
  );

  // Nothing to put in its place, so nothing is taken away. This is also what
  // the control's disabled state is keyed on (`selectHasCopyableOrderLines`);
  // the guarantee lives here so a caller that skipped the selector gets it too.
  if (!copyableIds.length) return;

  const replaced = Object.keys(getState().draftCart.cartItems ?? {}).length;
  if (replaced) dispatch(draftCartActions.emptyDraftCart());

  let lastDraftId = 0;
  copyableIds.forEach((sourceId) => {
    lastDraftId = dispatch(
      copyLineIntoDraft(
        cartItems[sourceId],
        cartItemsQuantity?.[sourceId] ?? 1,
        'copy_all'
      )
    );
  });

  // HITL-1704 — the last line copied, matching the takeover's "last promoted
  // line" (HITL-1683). Unconditional, which also retires the old pre-clear
  // deselect: `emptyDraftCart` renumbers from 1, so a draft selection held
  // across the press would otherwise point at a DIFFERENT line.
  dispatch(selectDraftLine(lastDraftId));

  const skipped = sourceIds.length - copyableIds.length;
  logger.info({
    message: 'Copied the whole Live order into My Cart',
    moreInfo: `${copyableIds.length} line(s) copied${
      skipped ? `, ${skipped} auto-combined line(s) skipped` : ''
    }${replaced ? `, replacing ${replaced} line(s) already in My Cart` : ''}`,
  });
};

/**
 * HITL-1615 — write one draft line into the order.
 *
 * Sole caller is `replaceOrderWithDraftCart` since HITL-1645 removed the
 * per-line promote. Kept as its own function: the three things it gets right
 * are about writing into `cartSlice`, not about replacing, and the next writer
 * into the live order will need all three.
 *
 * Three things it has to get right:
 *
 * 1. **A fresh id from `cartSlice`'s own `sequenceId`.** The two slices number
 *    independently and both start at 1.
 * 2. **`addedBy: human`.** This is not cosmetic. `useHypothesisFrames` disarms
 *    its intercept-import on the agent's first real edit, and that check is
 *    `addedBy !== AI` (authorship) OR a baseline key gone/changed (structural).
 *    An APPENDED line leaves every baseline key intact, so if the promoted item
 *    still said `addedBy: ai` neither branch would trip and the AI's marked
 *    `handoff_flush` frame could import straight over the agent's work. It is
 *    also simply true: the agent authored this line into the order.
 * 3. **Quantity carried over.** `addItemToCart` always sets 1.
 */
const writeLineToOrder =
  (cartItem: CartItem, quantity: number): AppThunk<number> =>
  (dispatch, getState) => {
    const { sequenceId, cartItems } = getState().cart;
    // `addItemToCart` keys on `cartItem.cartItemId` and overwrites whatever is
    // there, so an id that is already taken silently DESTROYS a line of the live
    // order — in `Intercept`, that is a POS-visible loss. `sequenceId` should
    // always be past the last id, but it is maintained by a different slice than
    // the one this item came from, so the max is taken rather than trusted.
    const takenIds = Object.keys(cartItems ?? {}).map(Number);
    const cartItemId = Math.max(sequenceId, ...takenIds.map((id) => id + 1), 1);
    // Shallow, for the same reason as `draftCopyOf`: a JSON round-trip of the
    // menu tree this item carries overflows the string limit.
    const promoted: CartItem = {
      ...cartItem,
      cartItemId,
      addedBy: CartItemAdditionTypes.human,
    };

    dispatch(cartActions.addItemToCart({ cartItem: promoted }));
    if (quantity > 1) {
      dispatch(
        cartActions.updateQuantity({
          cartItemId: cartItemId.toString(),
          quantity,
        })
      );
    }
    return cartItemId;
  };

/**
 * HITL-1615 — cart-level promote: REPLACE the order with My Cart.
 *
 * Replace, not append, is what avoids the duplicate trap: an agent who shadowed
 * the whole order would otherwise double every line.
 *
 * Deliberately NOT `clearCart()`. That returns `initialCartState` and would take
 * the coupons, loyalties and POS items with it — none of which this story is
 * replacing, and none of which the agent asked to lose. Only the ITEMS are
 * replaced, one delete at a time.
 *
 * Every dispatch here is synchronous inside one handler, so React commits them
 * as a single render: the cart is never observably empty between the deletes and
 * the writes, and `OrderReview`'s cart effect sees one transition rather than
 * two.
 */
export const replaceOrderWithDraftCart =
  (): AppThunk => (dispatch, getState) => {
    const { cartItems, cartItemsQuantity } = getState().draftCart;
    const draftIds = Object.keys(cartItems ?? {});
    if (!draftIds.length) return;

    const existingIds = Object.keys(getState().cart.cartItems ?? {});
    existingIds.forEach((id) => dispatch(cartActions.deleteCartItem(id)));

    // `writeLineToOrder` returns the id it assigned, so the last one falls out
    // of the loop rather than having to be re-derived from the cart afterwards.
    let lastPromotedId = 0;
    draftIds.forEach((id) => {
      lastPromotedId = dispatch(
        writeLineToOrder(cartItems[id], cartItemsQuantity?.[id] ?? 1)
      );
    });

    dispatch(draftCartActions.emptyDraftCart());

    /**
     * HITL-1685 — the Unresolved items panel describes the AI's cart, and that
     * cart has just been thrown away.
     *
     * Its cards are read off the AI's latest hypothesis frame, not off
     * `cartSlice`, so replacing the order changes nothing they show. And the
     * takeover stands the AI down, so no later frame arrives to correct them:
     * without this the agent's correct order sat under not-on-menu / invalid
     * option cards for the rest of the car.
     *
     * The panel's own Dismiss, not a reset — the session-summary log counts the
     * undismissed lists, so the analytics still record what the AI flagged.
     */
    dispatch(dismissAllUnresolvedItems());

    /**
     * HITL-1683 — leave the last promoted line selected, so the centre pane has
     * something in it the moment the takeover lands.
     *
     * Two reasons, and the second is the one that matters.
     *
     * The agent has just made this cart the order; the pane sitting empty makes
     * them click a line to get back to where they were. Selecting the last line
     * is the closest thing to "carry on from here".
     *
     * And the selection would otherwise be STALE or DANGLING. If they pressed
     * Take over while a My Cart line was open in the pane, the dialog is holding
     * that draft id with `cartScope: 'draft'` — and `emptyDraftCart` above has
     * just deleted it. The pane would be pointing at an item that no longer
     * exists, in a cart that is now empty.
     *
     * `MAIN_CART_SCOPE` explicitly, not by omission: after this the selection
     * addresses `cartSlice`, and being explicit is what stops the next reader
     * wondering whether the default still applies here.
     */
    const promoted = getState().cart.cartItems?.[lastPromotedId];
    if (promoted) {
      dispatch(
        dialogActions.selectItemAndResetNavigation({
          item: promoted,
          itemCartId: lastPromotedId,
          cartScope: MAIN_CART_SCOPE,
        })
      );
    }

    /**
     * HITL-1755 — no `sendOrder` here. `OrderReview` sends the replaced order.
     *
     * HITL-1683 added a send here because it believed nothing else would put
     * the new cart on the POS until a Finish or an `OrderReview` remount. That
     * was wrong in the layout this button lives in. The Live Order panel keeps
     * `OrderReview` mounted at all times (HITL-1614, `keepBodyMounted`), and
     * its cart-change effect fires on the render these writes cause, because
     * every line has just been deleted and rewritten. So both sent. Production
     * showed 83% of takeovers putting the same cart on the POS two or three
     * times within ~100 ms. The copies raced each other through menu-api and
     * posmon, often landing in reverse `seq` order, and the loser came back
     * `EUNKNOWN` (VI-1482).
     *
     * The effect is the sender to keep. It applies the same valid-lines guard
     * this send had, it goes through auto-combo when the store has it, and it
     * passes the menu-unification callbacks that this bare `sendOrder({})`
     * left out.
     */
    logger.info({
      message: 'Replaced the order with My Cart',
      moreInfo: `${existingIds.length} line(s) out, ${draftIds.length} in`,
    });
  };

/**
 * HITL-1692 — throw My Cart away without taking over with it.
 *
 * An agent in `Intercept` who carries on in the Live order instead of pressing
 * Take over had no way to close My Cart: the column only collapses once the
 * Intercept is theirs AND the draft is empty (`RightSidebar`'s
 * `shouldCollapseMyCart`). Emptying the draft is therefore the whole job — the
 * collapse follows from the rule that already exists, with no layout state of
 * its own and no risk of remounting the panel that holds `OrderReview`.
 *
 * Touches nothing but `draftCart` and the dialog. A draft never reaches the POS
 * and discarding it is not an announcement, so no `sendOrder`, no interception
 * and no TTS — which is also why the press needs no confirmation.
 *
 * The selection is cleared only when it points INTO My Cart. That id is about to
 * be deleted, and a centre pane left holding it would keep editing a line that
 * no longer exists (the same dangling selection HITL-1683 fixed on takeover). A
 * Live order selection is the agent's current work and is left exactly where it
 * is.
 */
export const discardDraftCart = (): AppThunk => (dispatch, getState) => {
  const { draftCart, dialog } = getState();
  const count = Object.keys(draftCart.cartItems ?? {}).length;
  if (!count) return;

  if (dialog.selectedItemCartScope === DRAFT_CART_SCOPE) {
    dispatch(dialogActions.resetDialogSelectedItem());
  }
  dispatch(draftCartActions.emptyDraftCart());

  logger.info({
    message: 'Discarded My Cart',
    moreInfo: `${count} line(s) discarded`,
  });
};

export default copyCartItemToDraft;
